/**
 * PSTUDIO-3610: pure decision — "may this tab reload its nodes right now?".
 * Kept free of store/React imports so every guard branch is unit-testable
 * (pro-studio has no RTL; the hook wiring is covered by e2e).
 */
export interface CrossTabReloadGuards {
  /** A sibling tab signalled changes for OUR solutionVersionId. */
  isDirty: boolean;
  isVisible: boolean;
  isTablePage: boolean;
  isReloadInFlight: boolean;
  /** tableStore.currentState — never reload over a loading table. */
  currentState: string;
  isCellBeingEdited: boolean;
  /** Local writes queued/in flight — a reload's snapshot could predate their
   *  commit and rows.replace would clobber them (one-shot replay race). */
  hasPendingSaves: boolean;
  lastReloadAt: number | null;
  now: number;
  minIntervalMs: number;
}

export const CROSS_TAB_RELOAD_MIN_INTERVAL_MS = 3_000;

/** Poll interval for retrying a reload blocked by a transient state that
 *  resolves WITHOUT firing any event we listen for (loading, in-progress cell
 *  edit, pending local saves). The throttle window has an exact deadline so it
 *  never uses this — see reloadRetryDelayMs. */
export const CROSS_TAB_RELOAD_RETRY_BACKOFF_MS = 1_000;

/** Ceiling for the decayed retry delay (see retryDelayWithBackoff). Same order
 *  as the feature's own tempo — the reload throttle is 3s — so a block that
 *  never clears costs a timer every few seconds instead of one every second,
 *  while a change still lands within seconds of the block lifting. */
export const CROSS_TAB_RELOAD_RETRY_MAX_BACKOFF_MS = 5_000;

export function shouldReloadNodes(g: CrossTabReloadGuards): boolean {
  if (!g.isDirty) return false;
  if (!g.isVisible || !g.isTablePage) return false;
  if (g.isReloadInFlight) return false;
  if (g.currentState === 'loading') return false;
  if (g.isCellBeingEdited) return false;
  if (g.hasPendingSaves) return false;
  if (g.lastReloadAt !== null && g.now - g.lastReloadAt < g.minIntervalMs) {
    return false;
  }
  return true;
}

/**
 * PSTUDIO-3625: when shouldReloadNodes says "no", decide whether the caller
 * should schedule its OWN retry, and after how long. A dropped signal keeps
 * `isDirty` true, but the hook only re-checks on broadcast / focus /
 * visibilitychange / an in-flight reload finishing — so a change that arrives
 * inside the 3s throttle window (e.g. a rapid collapse/expand series) is never
 * re-attempted until some unrelated event happens. That is why cross-tab sync
 * "doesn't always fire" for group collapse/expand.
 *
 * Returns:
 *   - null  → don't self-schedule. Either a reload is allowed now, there is
 *             nothing to sync, or the block is event-driven (hidden tab, off a
 *             table page, reload already in flight) so an existing
 *             listener/callback will re-trigger maybeReload.
 *   - >0 ms → the block WILL clear on its own with no event: wait this long and
 *             re-check. For the throttle it is the exact remaining window; for
 *             loading / cell-edit / pending-saves it is a fixed poll backoff.
 */
export function reloadRetryDelayMs(g: CrossTabReloadGuards): number | null {
  if (shouldReloadNodes(g)) return null;
  if (!g.isDirty) return null;
  if (!g.isVisible || !g.isTablePage) return null;
  if (g.isReloadInFlight) return null;
  // Everything below self-resolves with no event we listen for.
  if (g.lastReloadAt !== null) {
    const remaining = g.minIntervalMs - (g.now - g.lastReloadAt);
    if (remaining > 0) return remaining;
  }
  return CROSS_TAB_RELOAD_RETRY_BACKOFF_MS;
}

/**
 * PSTUDIO-4168 (review follow-up): decay the self-scheduled retry instead of
 * abandoning it.
 *
 * The retry exists because some blocks clear with no event this tab listens
 * for. A hard cap on consecutive retries looked like a cheap way to stop a
 * block that NEVER clears from costing a timer per second for the life of the
 * session — but it also abandons the blocks the retry was written for. Closing
 * a cell edit fires neither `focus` nor `visibilitychange`, and a teammate who
 * made one change sends no second broadcast, so once the budget was spent the
 * change sat undrained. One cell edit held open for a minute was enough.
 *
 * Decaying the delay buys the same protection without the failure mode: the
 * cost of a permanently blocked tab falls to a timer every few seconds, and a
 * block that lifts after any amount of time is still picked up.
 *
 * `baseMs` wins when it is already longer than the floor, so the throttle path
 * — which passes the exact remaining window — is never re-checked early. A
 * long-running block overshooting that window is harmless: consecutive retries
 * only accumulate while blocked, and a dispatch resets them to zero, so a
 * throttle-only wait never reaches the larger floors.
 */
export function retryDelayWithBackoff(
  baseMs: number,
  consecutiveRetries: number
): number {
  const floor = Math.min(
    CROSS_TAB_RELOAD_RETRY_BACKOFF_MS * 2 ** Math.max(0, consecutiveRetries),
    CROSS_TAB_RELOAD_RETRY_MAX_BACKOFF_MS
  );
  return Math.max(baseMs, floor);
}

/**
 * PSTUDIO-4168 (review follow-up): the store/route mismatch branch defers a
 * reload outside `shouldReloadNodes`, so it has to repeat that function's
 * reasoning about WHICH blocks are worth polling. A hidden tab or a tab off a
 * table page is blocked by something event-driven — `visibilitychange` and
 * `focus` both re-enter `maybeReload` — so polling it buys nothing and, before
 * this, kept a backgrounded tab re-checking once a second until it was looked
 * at again. Mirrors the corresponding early returns in `reloadRetryDelayMs`.
 */
export function shouldRetryWhileVersionMismatched(
  g: Pick<CrossTabReloadGuards, 'isDirty' | 'isVisible' | 'isTablePage'>
): boolean {
  return g.isDirty && g.isVisible && g.isTablePage;
}

/**
 * PSTUDIO-4189 (review): may pending cross-tab dirt still be acted on?
 *
 * A broadcast is matched against the store once, when it arrives, and the tab
 * then carries a dirty flag until it can reload. That flag has to remember WHICH
 * version raised it. PSTUDIO-4168 hoisted `useCrossTabNodeSync` out of the
 * `isLoading` branch so its flag would survive a publish — which also makes it
 * survive a full solution switch, because `BuilderPage` is keyed by route label
 * and serves any two solutions from the same component instance. Dirt raised for
 * solution A and blocked (a cell edit, the throttle) would otherwise still be
 * set when the store reaches solution B, satisfy the route/store guard for B,
 * and dispatch a `reloadNodes` — a fetch and a `validateAndSync` compile of B —
 * for a change that happened in A.
 *
 * Retargeting it is never right; the change belongs to a version this tab has
 * left. Dropping it loses nothing across a publish bump either, for the reason
 * PSTUDIO-4168 pins in the store: the sibling's edit committed to the old
 * version before the bump, and the publish clone carries it forward.
 *
 * `null` (no dirt) answers false — there is nothing to keep.
 */
export function pendingDirtSurvives(
  dirtyForSolutionVersionId: string | null,
  storeSolutionVersionId: string
): boolean {
  if (dirtyForSolutionVersionId === null) return false;
  if (!storeSolutionVersionId) return false;
  return dirtyForSolutionVersionId === storeSolutionVersionId;
}
